import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Book, Pencil } from 'lucide-react';
import { apiErrorCode, emberApi, type MePresenceResponse, type MeSummaryResponse } from '../api';
import { trackFailed } from '../../../../../shared-web/track';
import { useRequireAuth } from '../use-require-auth';
import { useTalkSheet } from '../talk-sheet';
import { PageBack } from '../components/PageBack';
import { NotificationBadge } from '../components/NotificationBadge';
import { PageTabs } from '../components/PageTabs';
import { PresenceAvatar } from '../components/PresenceAvatar';
import { AvatarSheet } from '../components/AvatarSheet';
import { Modal } from '../components/Modal';
import { Notebook, NotebookLockRow } from '../components/Notebook';
import {
  markClosed, openedThisSession, readLegacyLock, removeLegacyLock, NO_RECOVERY, NO_REAUTH, webReauthMethods,
  type NotebookLockState,
} from '../notebook-lock';
import { MyEmbersPage } from './MyEmbers';
import { LettersPage } from './Letters';
import { AddToHomeButton } from '../../../../../shared-web/AddToHomeScreen';
import './MeShell.css';

export interface MeShellProps {
  tab: 'embers' | 'letters';
  path: string;
}

/**
 * S10 — Profile Shell (§4.10, 2026-09-01 redesign): one page skin, an
 * identity header, two tabs. The header is the person, not the page: their
 * presence picture (tap to change it right here), their line as the title
 * (pencil to edit it right here — the retired /settings/space page's editor,
 * moved home 2026-09-01), tenure, and the number that says what passed
 * through them. Embers leads and /me lands there. The page is the notebook
 * (§4.12) and is called that everywhere it is named — the menu, the cover,
 * this header before a line is written, the browser title (2026-09-25).
 */
export function MeShell({ tab, path }: MeShellProps): ReactElement {
  const status = useRequireAuth(path);
  const openTalk = useTalkSheet();
  const [summary, setSummary] = useState<MeSummaryResponse | null>(null);
  const [picking, setPicking] = useState(false);
  // The line editor sheet. `presence` loads when the pencil first opens it —
  // the open suggestion batch (Ideas from your Embers) rides that read.
  const [lineOpen, setLineOpen] = useState(false);
  const [presence, setPresence] = useState<MePresenceResponse | null>(null);
  const [draft, setDraft] = useState('');
  const [fromSuggestion, setFromSuggestion] = useState(false);
  const [lineError, setLineError] = useState('');
  const [lineBusy, setLineBusy] = useState(false);

  /**
   * The notebook (§4.12, 2026-09-04 拍板). /me is a book, closed on arrival:
   * the cover takes the whole page once a browser session, and the Profile —
   * header, tabs and all — is what is inside it. Unless the person has locked
   * it, the cover opens on its own — arriving here should hand them their
   * pages, not a question. `null` is "not asked yet", which only lasts until
   * the first client effect; the store this reads exists nowhere else, so it
   * can never be answered on the server.
   */
  const [notebookOpen, setNotebookOpen] = useState<boolean | null>(null);
  /** True only for the render the cover just left, to stagger the pages in. */
  const [justOpened, setJustOpened] = useState(false);
  /** True while the cover is swinging shut after `Close the notebook`. */
  const [closing, setClosing] = useState(false);
  /**
   * The one lock, read here and handed to the corner, the cover and the row at
   * the foot — they all change the same one, so taking it off in one place is
   * seen at once by the others. It is the ACCOUNT's lock since 2026-09-07, so
   * it is read from the server; `null` is "not read yet", and a read that
   * fails leaves the page closed rather than open. Failing closed costs
   * nothing here: the pages behind the cover come from the same API, so an
   * edge we cannot reach has no notebook to show either.
   */
  const [lock, setLock] = useState<NotebookLockState | null>(null);
  const [lockFailed, setLockFailed] = useState(false);
  /** The sheet that gives a notebook without a password one on its way shut. */
  const [closeAsk, setCloseAsk] = useState(false);
  const [closeDraft, setCloseDraft] = useState('');
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState('');
  const readLockState = useCallback(() => {
    setLockFailed(false);
    emberApi.notebookLock()
      .then((next) => {
        // A pre-2026-09-07 lock still in this browser only means anything
        // while the account has none: once the account is locked, that old
        // record can never be the answer again, so it is dropped here rather
        // than left to ask a second password behind the first.
        const legacy = readLegacyLock();
        if (next.locked && legacy) removeLegacyLock();
        setLock({
          locked: next.locked,
          legacy: next.locked ? null : legacy,
          recovery: next.recovery || NO_RECOVERY,
          reauth: next.reauth || NO_REAUTH,
        });
      })
      .catch(() => setLockFailed(true));
  }, []);
  useEffect(() => {
    setNotebookOpen(openedThisSession());
  }, []);
  // The lock is the account's, so it is read once the session says there is
  // one: a visitor who lands on /me is on their way to /auth (useRequireAuth),
  // and asking for their lock first only put a 401 in every such console
  // (dev 2026-10-01 全流程走查).
  useEffect(() => {
    if (!import.meta.env.DEV && status !== 'authenticated') return;
    readLockState();
  }, [status, readLockState]);
  const openNotebook = useCallback(() => {
    setNotebookOpen(true);
    setJustOpened(true);
    setClosing(false);
  }, []);
  /**
   * Closing the notebook (2026-09-05): forget this session's opening, and hand
   * the page back to the cover, which swings shut and then asks. Closing and
   * locking are one gesture — a notebook without a password is asked for one
   * first (`closeAsk`), because a closed book that opens by itself is not
   * closed.
   */
  const closeNotebook = useCallback(() => {
    markClosed();
    setJustOpened(false);
    setClosing(true);
    setNotebookOpen(false);
  }, []);
  const onCloseTap = (): void => {
    if (lock?.locked) closeNotebook();
    else setCloseAsk(true);
  };
  const submitCloseAsk = (): void => {
    if (closeBusy || !closeDraft.trim() || !lock) return;
    setCloseBusy(true);
    setCloseError('');
    emberApi.setNotebookLock({ password: closeDraft, source: 'close' })
      .then(() => {
        setLock({ ...lock, locked: true });
        setCloseAsk(false);
        setCloseDraft('');
        closeNotebook();
      })
      .catch((err: unknown) => {
        setCloseError(
          apiErrorCode(err) === 'password_too_short'
            ? 'A little longer — at least four characters.'
            : 'That didn’t go through. Try again.',
        );
        trackFailed('notebook_lock_set', apiErrorCode(err) || 'request_failed');
      })
      .finally(() => setCloseBusy(false));
  };

  useEffect(() => {
    if (!import.meta.env.DEV && status !== 'authenticated') return undefined;
    let alive = true;
    emberApi.meSummary()
      .then((next) => { if (alive) setSummary(next); })
      // The header is additive: a failed summary quiets it to the fallback
      // title and the tabs still work, so no failure surface here.
      .catch(() => {});
    return () => { alive = false; };
  }, [status]);

  if (!import.meta.env.DEV && status !== 'authenticated') {
    return <div className="ember-page-quiet">One moment…</div>;
  }

  if (lockFailed) {
    return (
      <div className="ember-container me-shell">
        <p className="ember-page-quiet">
          We couldn’t reach your notebook just now.{' '}
          <button type="button" className="ember-text-action" onClick={readLockState}>Try again</button>
        </p>
      </div>
    );
  }
  if (notebookOpen === null || lock === null) return <div className="ember-page-quiet">One moment…</div>;
  if (!notebookOpen) {
    return (
      <div className="ember-container me-shell">
        {/* The cover's way out — a locked notebook had none: the open book's own Back, in the same place. */}
        <div className="me-toolbar is-cover"><PageBack className="me-back" /></div>
        <Notebook state={lock} onLockChange={setLock} onOpen={openNotebook} closing={closing} />
      </div>
    );
  }


  const openLineEditor = (): void => {
    setLineOpen(true);
    setDraft(summary?.line || '');
    setFromSuggestion(false);
    setLineError('');
    if (!presence) {
      emberApi.mePresence()
        .then((next) => {
          setPresence(next);
          // The durable line is the presence read's truth; the summary may be
          // a beat behind it.
          setDraft((current) => current || next.line || '');
        })
        .catch(() => { /* the input still works; only the idea chips are lost */ });
    }
  };

  const saveLine = (): void => {
    if (lineBusy) return;
    const suggestion = presence?.suggestions[0] || null;
    setLineBusy(true);
    setLineError('');
    emberApi.patchMePresence(
      fromSuggestion && suggestion
        ? { line: draft, suggestion_id: suggestion.id }
        : { line: draft },
    )
      .then((next) => {
        setPresence(next);
        setSummary((current) => (current ? { ...current, line: next.line } : current));
        setLineOpen(false);
      })
      .catch((err: unknown) => {
        if (apiErrorCode(err) === 'presence_line_qa_failed') {
          setLineError('That line can’t be used — keep it in English, without names, places, or contact details.');
        } else {
          trackFailed('presence_patch', 'patch_failed', {});
          setLineError('That didn’t go through. Try again.');
        }
      })
      .finally(() => setLineBusy(false));
  };

  return (
    <div className={`ember-container me-shell${justOpened ? ' me-shell-revealed' : ''}`}>
      <div className="me-toolbar">
        <PageBack className="me-back" />
        <div className="me-toolbar-actions">
          {/* Install left this toolbar (2026-09-05 walkthrough: two text links
              up here gave installing the app the weight of closing the book);
              it sits at the notebook's foot now. */}
          {/* The corner closes the book (2026-09-05, replacing the Settings
              step that lived here — Settings is the account and keeps its row
              in the top-nav account menu; this page is a notebook, and what a
              notebook's corner does is close). Same control family as Install
              beside it (§4 quiet icon + text): one toolbar never mixes two.
              `state` says what the tap did: shut a locked notebook at once, or
              ask a password of one that had none. */}
          <button
            type="button"
            className="me-toolbar-action is-close"
            data-log="notebook_close"
            data-log-state={lock.locked ? 'shut' : 'ask'}
            onClick={onCloseTap}
          >
            <Book size={16} strokeWidth={2} aria-hidden />
            <span>Close the notebook</span>
          </button>
        </div>
      </div>

      <header className="me-identity">
        <div className="me-identity-lead">
          <button
            type="button"
            className="me-avatar-button"
            aria-label="Change your picture"
            data-log="me_avatar_open"
            onClick={() => setPicking(true)}
          >
            {summary
              ? <PresenceAvatar avatarKey={summary.avatar_id || ''} size={72} />
              : <span className="me-avatar-blank" aria-hidden />}
          </button>
          {/* The telling entrance, moved off the toolbar into the header
              (2026-09-01) — sitting right beside the person, where the page's
              one real invitation belongs. */}
          <button
            type="button"
            className="ember-btn ember-btn-secondary me-talk-button"
            onClick={() => openTalk({ returnTo: path })}
            data-log="talk_open"
            data-log-discovery="me"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M10 17c3 0 5.5-2.3 5.5-5.2 0-2-1.1-3.5-2.3-4.8-1-1.2-2-2.5-2.3-4.2C9 3.9 8.2 5.5 8.4 7.2c.1.8-.5 1-1 .5-.4-.4-.7-.9-.9-1.5-1.2 1.3-1.8 2.7-1.8 4.2C4.7 14.8 7 17 10 17z" />
            </svg>
            <span>Talk to Ember</span>
          </button>
        </div>
        <div className="me-identity-copy">
          <div className="me-title-row">
            <h1 className="me-title" dir="auto">
            {summary ? (summary.line || 'Your notebook') : ' '}
            </h1>
            {/* The line editor lives where the line shows (2026-09-01 拍板,
                retiring /settings/space): same in-place continuity as the
                avatar button beside it. */}
            {summary && (
              <button
                type="button"
                className="me-line-edit"
                aria-label="Edit your line"
                data-log="presence_space_open"
                data-log-discovery="me"
                onClick={openLineEditor}
              >
                <Pencil size={15} strokeWidth={1.75} aria-hidden />
              </button>
            )}
          </div>
          <p className="me-since">
            {summary ? (
              <>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="14" r="4" />
                  <path d="M12 4v3" />
                  <path d="M5 7l2 2" />
                  <path d="M19 7l-2 2" />
                  <path d="M3 20h18" />
                </svg>
                <span>Here since {summary.since_month}</span>
              </>
            ) : ' '}
          </p>
        </div>
        <div className="me-stats">
          {summary && (
            <>
              <div className="me-stat">
                <span className="me-stat-number">{summary.embers_told}</span>
                <span className="me-stat-label">{summary.embers_told === 1 ? 'Ember told' : 'Embers told'}</span>
              </div>
            </>
          )}
        </div>
      </header>

      {/* The unread badges live on the tabs that clear them: every letter
          about an Ember on Embers (the cards carry them as door lines, §4.9;
          since 2026-09-25 the reader's `mattered` among them — the Mattered
          tab that used to count it folded into this one). The top icon's
          total is the tabs added up, with the rooms in the Chats list. */}
      <PageTabs
        className="me-tabs"
        label="Notebook tabs"
        active={tab}
        items={[
          {
            key: 'embers',
            label: 'Embers',
            to: '/me/embers',
            log: 'me_tab_embers',
            badge: <NotificationBadge className="ember-tab-badge" kind="knocks" />,
          },
          // Ember's weekly letters (§4.20, 2026-09-23): what Ember saw in
          // the Embers this person wrote that week. The badge is the
          // letters not yet opened here.
          // (Questions and Rooms left the notebook 2026-10-20: Ask people
          // stopped on 2026-09-26, and the rooms are in the Chats list.)
          {
            key: 'letters',
            label: 'Letters',
            to: '/me/letters',
            log: 'me_tab_letters',
            badge: <NotificationBadge className="ember-tab-badge" kind="letters" />,
          },
        ]}
      />
      {tab === 'letters' ? <LettersPage /> : <MyEmbersPage />}

      {/* Change or drop the password, once there is one. It lives with the
          book it closes, not in account Settings: it is a property of this
          browser, and Settings is the account. */}
      <NotebookLockRow state={lock} onLockChange={setLock} />
      <div className="me-install-row">
        <AddToHomeButton
          className="me-toolbar-action is-install"
          label="Install Ember on your phone"
          ariaLabel="Add Ember to your Home Screen"
          source="profile"
        />
      </div>
      {picking && (
        <AvatarSheet
          avatarKey={summary?.avatar_id || ''}
          onSaved={(avatarId) => setSummary((current) => (current ? { ...current, avatar_id: avatarId } : current))}
          onClose={() => setPicking(false)}
        />
      )}

      {closeAsk && (
        <Modal
          onClose={() => { if (!closeBusy) { setCloseAsk(false); setCloseDraft(''); } }}
          labelledBy="me-close-title"
          sheet="phone"
          className="me-close-sheet"
        >
          <h2 id="me-close-title" className="me-avatar-sheet-title">Close the notebook</h2>
          <p className="me-avatar-sheet-help">
            Give it a password only you know. It keeps this page closed on any
            phone or browser you sign in from — your Embers are kept by your
            account either way.
          </p>
          {/* Said here, where the password is chosen, because this is the only
              moment the warning can change anything. Since 2026-10-19 a
              forgotten password is reset by signing in again, so the warning is
              only for an account this browser has no way to do that for. */}
          {!lock.recovery.available && webReauthMethods(lock).length === 0 && (
            <p className="me-avatar-sheet-help">
              {lock.reauth.methods.length > 0
                ? 'If you forget it, you can reset it in the Ember app.'
                : lock.recovery.reason === 'opted_out'
                  ? 'You asked us to stop texting you, so if you forget this password we can’t text you a way back in. Text your Ember line anything at all and we can again.'
                  : 'This account has no phone connected, so if you forget this password there’s nowhere to text a way back in. Connect one in Settings — turn on Text messages — first.'}
            </p>
          )}
          <form
            onSubmit={(event) => { event.preventDefault(); submitCloseAsk(); }}
          >
            <input
              className="me-line-input"
              type="password"
              value={closeDraft}
              placeholder="A password only you know"
              autoComplete="new-password"
              aria-label="Notebook password"
              onChange={(event) => { setCloseDraft(event.target.value); setCloseError(''); }}
            />
            {closeError && <p className="me-line-error" role="alert">{closeError}</p>}
            <div className="me-avatar-sheet-actions">
              <button
                type="button"
                className="ember-text-action"
                onClick={() => { setCloseAsk(false); setCloseDraft(''); }}
                disabled={closeBusy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ember-btn ember-btn-primary"
                disabled={closeBusy || !closeDraft.trim()}
                data-log="notebook_lock_set"
                data-log-state="close"
              >
                Close it
              </button>
            </div>
          </form>
        </Modal>
      )}

      {lineOpen && (
        <Modal
          onClose={() => { if (!lineBusy) setLineOpen(false); }}
          labelledBy="me-line-title"
          sheet="phone"
          className="me-line-sheet"
        >
          <h2 id="me-line-title" className="me-avatar-sheet-title">A line about you</h2>
          <p className="me-avatar-sheet-help">
            One short sentence in English, at the top of your notebook.
          </p>
          <input
            className="me-line-input"
            type="text"
            value={draft}
            maxLength={120}
            placeholder="A parent. A builder. Someone learning to begin again."
            aria-label="A line about you"
            onChange={(event) => {
              setDraft(event.target.value);
              setFromSuggestion(false);
            }}
          />
          {presence?.suggestions[0] ? (
            <div className="me-line-ideas">
              <p className="me-line-ideas-title">Ideas from your Embers</p>
              <div className="me-line-chips">
                {presence.suggestions[0].lines.map((line) => (
                  <button
                    key={line}
                    type="button"
                    className="me-line-chip"
                    disabled={lineBusy}
                    data-log="presence_line_use"
                    onClick={() => { setDraft(line); setFromSuggestion(true); }}
                  >
                    <span dir="auto">{line}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {lineError ? <p className="me-line-error">{lineError}</p> : null}
          <div className="me-avatar-sheet-actions">
            <button
              type="button"
              className="ember-text-action"
              onClick={() => setLineOpen(false)}
              disabled={lineBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ember-btn ember-btn-primary"
              disabled={lineBusy || !draft.trim() || draft.trim() === (summary?.line || '')}
              data-log="presence_line_edit_save"
              onClick={saveLine}
            >
              Save line
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
